// v2/components/ModalQRPagoV2.jsx
//
// Modal del flujo presencial: el admin presiona "Generar QR", obtiene
// token+url REAL de Webpay (a través del backend v2), renderiza el QR
// y espera a que el cliente complete el pago.
//
// Cuando el admin presiona "Verificar pago", el backend consulta a
// Transbank con el token y devuelve el estado real (aprobado/rechazado).
//
// Reglas de dinero que cuida este modal:
//  - El QR lleva `token_ws`: sin él, el link de Webpay no abre ninguna
//    transacción. Se dibuja localmente (antes se mandaba la URL de pago a un
//    servicio de terceros para generar la imagen).
//  - Cerrar el modal NO descarta el QR: el cliente puede seguir pagando en su
//    teléfono. El estado se conserva y el padre recibe `pendiente` para bloquear
//    los otros medios de pago (evita cobrar dos veces: Webpay + POS).
//  - `requiere_revision` significa que el dinero SÍ llegó pero algo falló al
//    inscribir: no se ofrece reintentar ni cobrar de nuevo.
//  - `rechazada`: el backend cancela la venta y libera los cupos, así que no se
//    puede generar otro QR sobre ella; hay que iniciar una venta nueva.

import React, { useEffect, useState } from 'react';
import { App, Modal, Button, Spin } from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import {
  QrcodeOutlined, CheckCircleFilled, CloseCircleFilled,
  LoadingOutlined, ReloadOutlined, WarningOutlined, ExclamationCircleFilled,
  LinkOutlined, CopyOutlined
} from '@ant-design/icons';
import VentaPresencialV2 from '../../../../service/ventaPresencial.service';
import { codigoError, mensajeDeError } from '../../../../service/client';

const clp = (n) => `$${Number(n || 0).toLocaleString('es-CL')}`;

// Errores al iniciar tras los cuales generar otro QR no sirve: la venta ya no
// está en curso (expiró, se canceló o ya se pagó).
const ERRORES_SIN_REINTENTO = ['VP_ESTADO_INVALIDO', 'VP_RESERVA_EXPIRADA', 'VP_NO_EXISTE', 'VP_SIN_MONTO'];

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.ventaPresencialId
 * @param {string} [props.codigoVenta]
 * @param {number} [props.monto]
 * @param {string} [props.returnUrl] - URL pública a la que Webpay redirige al cliente
 * @param {(resultado: object) => void} props.onCompletado - callback con {estado, ...}
 * @param {(estado: 'libre'|'pendiente'|'rechazada'|'no_cobrado'|'revision'|'completada') => void} [props.onEstadoPago]
 */
export default function ModalQRPagoV2({
  open,
  onClose,
  ventaPresencialId,
  codigoVenta,
  monto,
  returnUrl,
  onCompletado,
  onEstadoPago
}) {
  const { modal } = App.useApp();
  // idle | iniciando | esperando | verificando | completado | rechazado | noCobrado | revision | errorInicio
  const [estado, setEstado]       = useState('idle');
  const [datosQR, setDatosQR]     = useState(null); // { token, url, monto, ... }
  const [resultado, setResultado] = useState(null); // resultado de confirmar
  const [error, setError]         = useState(null);
  const [copiado, setCopiado]     = useState(false);

  // Si cambia la venta (venta nueva), el QR anterior deja de aplicar.
  useEffect(() => {
    setEstado('idle'); setDatosQR(null); setResultado(null); setError(null);
  }, [ventaPresencialId]);

  const linkPago = datosQR?.url && datosQR?.token
    ? `${datosQR.url}${datosQR.url.includes('?') ? '&' : '?'}token_ws=${encodeURIComponent(datosQR.token)}`
    : null;
  const montoACobrar = datosQR?.monto ?? monto;

  const handleGenerarQR = async () => {
    if (!returnUrl) {
      setError({ mensaje: 'Falta returnUrl para Webpay' });
      setEstado('errorInicio');
      return;
    }
    try {
      setEstado('iniciando');
      setError(null);
      const out = await VentaPresencialV2.iniciarPagoWebpay({ ventaPresencialId, returnUrl });
      if (!out?.token || !out?.url) throw new Error('Webpay no devolvió el token de pago');
      setDatosQR(out);
      setEstado('esperando');
      onEstadoPago?.('pendiente');
    } catch (err) {
      const code = codigoError(err);
      setError({ code, mensaje: mensajeDeError(err, 'No se pudo generar el QR'), sinReintento: ERRORES_SIN_REINTENTO.includes(code) });
      setEstado('errorInicio');
    }
  };

  const handleVerificar = async () => {
    if (!datosQR?.token) return;
    try {
      setEstado('verificando');
      setError(null);
      const out = await VentaPresencialV2.confirmarPagoWebpay(datosQR.token);
      setResultado(out);
      if (out.estado === 'completada') {
        setEstado('completado');
        onEstadoPago?.('completada');
      } else if (out.estado === 'rechazada') {
        setEstado('rechazado');
        onEstadoPago?.('rechazada');
      } else if (out.estado === 'requiere_revision') {
        setEstado('revision');
        onEstadoPago?.('revision');
      } else if (out.estado === 'no_cobrado') {
        // El backend no confirmó el cobro (la venta ya no estaba en curso):
        // Transbank reversa la autorización.
        setEstado('noCobrado');
        onEstadoPago?.('no_cobrado');
      } else {
        setEstado('esperando');
        setError({ mensaje: 'El pago aún no se confirma. Pídele al cliente que termine en su celular y vuelve a verificar.' });
      }
    } catch (err) {
      const code = codigoError(err);
      // Caso típico: el cliente aún no pagó, Transbank devuelve error.
      // Mantener el modal en estado "esperando" para reintentar.
      setEstado('esperando');
      if (code === 'IDEM_EN_CURSO') {
        setError({ mensaje: 'Otro proceso está confirmando este pago. Espera unos segundos y vuelve a verificar.' });
      } else if (code === 'WEBPAY_COMMIT_FALLO') {
        setError({ mensaje: 'El cliente aún no completó el pago. Pídele que termine en su celular y vuelve a verificar.' });
      } else {
        setError({ code, mensaje: mensajeDeError(err) });
      }
    }
  };

  // Cerrar: el QR pendiente se conserva (el cliente puede seguir pagando).
  const handleClose = () => {
    if (estado === 'iniciando' || estado === 'verificando') return;
    if (estado === 'completado') {
      onCompletado?.(resultado);
      return;
    }
    if (estado === 'errorInicio') { setEstado('idle'); setError(null); }
    onClose?.();
  };

  // Descartar el QR solo cuando el vendedor confirmó con el cliente que no pagó.
  const handleDescartar = () => {
    modal.confirm({
      title: '¿Descartar este QR?',
      icon: <ExclamationCircleFilled style={{ color: '#d97706' }} />,
      content: (
        <div className="text-sm text-slate-600 space-y-2">
          <p>Hazlo solo si el cliente <strong>no pagó</strong>: pídele que cierre Webpay en su teléfono sin completar el pago.</p>
          <p>Si igual paga después, el cobro llegará a esta venta y quedará en revisión. No cobres por otro medio hasta estar seguro.</p>
        </div>
      ),
      okText: 'Sí, descartar',
      cancelText: 'Seguir esperando',
      okButtonProps: { danger: true },
      onOk: () => {
        setEstado('idle'); setDatosQR(null); setError(null);
        onEstadoPago?.('libre');
        onClose?.();
      },
    });
  };

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(linkPago);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch { /* sin permiso de portapapeles: el link queda visible igual */ }
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={handleClose}
      closable={estado !== 'iniciando' && estado !== 'verificando'}
      maskClosable={false}
      footer={null}
      width={460}
      centered
      styles={{ content: { padding: 0, overflow: 'hidden' } }}
    >
      {/* Encabezado de marca */}
      <div className="bg-[#00266b] px-5 py-4 text-white">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#fae57e]">
          <QrcodeOutlined /> Pago con Webpay QR
        </div>
        <div className="mt-1 flex items-end justify-between gap-3">
          <div className="text-2xl font-bold leading-tight">{clp(montoACobrar)}</div>
          {codigoVenta && <div className="font-mono text-xs text-[#b9c8e8]">{codigoVenta}</div>}
        </div>
      </div>

      <div className="px-5 py-5">
        {estado === 'idle' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-[#fde68a] bg-[#fffbea] px-4 py-3 text-sm text-[#5c4300]">
              <p className="font-semibold mb-1">Vas a iniciar un cobro real con Webpay</p>
              <p className="text-xs leading-relaxed">
                Se crea una transacción en Transbank por el monto indicado. El cliente escanea el QR
                con su celular y paga ahí. Mientras el QR esté activo no podrás cobrar por otro medio.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button onClick={handleClose}>Cancelar</Button>
              <Button type="primary" icon={<QrcodeOutlined />} onClick={handleGenerarQR}>Generar QR</Button>
            </div>
          </div>
        )}

        {(estado === 'iniciando' || estado === 'verificando') && (
          <Centrado>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 36, color: '#0047bb' }} spin />} />
            <p className="mt-4 text-sm font-medium text-slate-600">
              {estado === 'iniciando' ? 'Generando QR…' : 'Verificando con Transbank…'}
            </p>
          </Centrado>
        )}

        {estado === 'esperando' && linkPago && (
          <div className="flex flex-col items-center">
            <div className="rounded-2xl border-4 border-[#feda2c] bg-white p-3">
              <QRCodeSVG value={linkPago} size={232} level="M" marginSize={1} title="QR de pago Webpay" />
            </div>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#fffbea] border border-[#fde68a]
              px-3 py-1 text-xs font-semibold text-[#5c4300]">
              <span className="h-2 w-2 rounded-full bg-[#feda2c] animate-pulse" /> Esperando pago del cliente
            </span>
            <p className="mt-3 text-center text-sm text-slate-500 max-w-xs">
              Pide al cliente que escanee el código con la cámara y complete el pago.
              Cuando termine, presiona <strong className="text-slate-700">Verificar pago</strong>.
            </p>

            <div className="mt-3 flex w-full items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
              <LinkOutlined className="text-slate-400" />
              <span className="flex-1 truncate text-xs text-slate-500" title={linkPago}>{linkPago}</span>
              <Button size="small" type="text" icon={<CopyOutlined />} onClick={copiarLink}>
                {copiado ? 'Copiado' : 'Copiar'}
              </Button>
            </div>

            {error && (
              <div className="mt-3 w-full flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <WarningOutlined className="mt-0.5" /> <span>{error.mensaje}</span>
              </div>
            )}

            <div className="mt-5 flex w-full items-center justify-between gap-2">
              <Button type="text" danger size="small" onClick={handleDescartar}>Descartar QR</Button>
              <div className="flex gap-2">
                <Button onClick={handleClose}>Cerrar</Button>
                <Button type="primary" icon={<ReloadOutlined />} onClick={handleVerificar}>Verificar pago</Button>
              </div>
            </div>
          </div>
        )}

        {estado === 'completado' && (
          <Centrado>
            <CheckCircleFilled style={{ fontSize: 56, color: '#0047bb' }} />
            <h3 className="mt-3 text-lg font-bold text-slate-800">¡Pago confirmado!</h3>
            <p className="text-sm text-slate-500">
              Se generaron {resultado?.inscripcionIds?.length || 0} inscripción(es).
            </p>
            <p className="mt-1 text-xs text-slate-400">La boleta y el correo se procesan en los próximos minutos.</p>
            <Button type="primary" className="mt-5" onClick={handleClose}>Listo</Button>
          </Centrado>
        )}

        {estado === 'revision' && (
          <Centrado>
            <ExclamationCircleFilled style={{ fontSize: 56, color: '#d97706' }} />
            <h3 className="mt-3 text-lg font-bold text-slate-800">Pago recibido, en revisión</h3>
            <p className="text-center text-sm text-slate-500 max-w-xs">
              Transbank aprobó el cobro, pero la inscripción no pudo completarse automáticamente.
            </p>
            <div className="mt-3 w-full rounded-lg border border-[#fde68a] bg-[#fffbea] px-3 py-2 text-xs text-[#5c4300]">
              <strong>No vuelvas a cobrar.</strong> El caso queda en Operaciones para resolverlo
              {codigoVenta ? <> (venta <span className="font-mono">{codigoVenta}</span>)</> : null}.
            </div>
            <Button type="primary" className="mt-5" onClick={handleClose}>Entendido</Button>
          </Centrado>
        )}

        {estado === 'rechazado' && (
          <Centrado>
            <CloseCircleFilled style={{ fontSize: 56, color: '#dc2626' }} />
            <h3 className="mt-3 text-lg font-bold text-slate-800">Webpay rechazó el pago</h3>
            {resultado?.motivo && <p className="text-center text-sm text-slate-500 max-w-xs">{resultado.motivo}</p>}
            <div className="mt-3 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              No se cobró nada. La venta quedó cancelada y sus cupos se liberaron: para intentarlo
              otra vez, inicia una venta nueva.
            </div>
            <Button className="mt-5" onClick={handleClose}>Cerrar</Button>
          </Centrado>
        )}

        {estado === 'noCobrado' && (
          <Centrado>
            <ExclamationCircleFilled style={{ fontSize: 56, color: '#64748b' }} />
            <h3 className="mt-3 text-lg font-bold text-slate-800">El pago no se realizó</h3>
            <p className="text-center text-sm text-slate-500 max-w-xs">
              {resultado?.motivo || 'La venta ya no estaba activa.'}
            </p>
            <div className="mt-3 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              No se confirmó el cobro en Transbank, así que al cliente no se le cobra. Inicia una venta nueva.
            </div>
            <Button className="mt-5" onClick={handleClose}>Cerrar</Button>
          </Centrado>
        )}

        {estado === 'errorInicio' && (
          <Centrado>
            <CloseCircleFilled style={{ fontSize: 56, color: '#dc2626' }} />
            <h3 className="mt-3 text-lg font-bold text-slate-800">No se pudo generar el QR</h3>
            <p className="text-center text-sm text-slate-500 max-w-xs">{error?.mensaje}</p>
            {error?.sinReintento && (
              <p className="mt-2 text-center text-xs text-slate-400 max-w-xs">
                La venta ya no está en curso. Inicia una venta nueva.
              </p>
            )}
            <div className="mt-5 flex gap-2">
              <Button onClick={handleClose}>Cerrar</Button>
              {!error?.sinReintento && (
                <Button type="primary" icon={<ReloadOutlined />} onClick={handleGenerarQR}>Reintentar</Button>
              )}
            </div>
          </Centrado>
        )}
      </div>
    </Modal>
  );
}

function Centrado({ children }) {
  return <div className="flex flex-col items-center justify-center py-4">{children}</div>;
}
